import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const APP_MEET_WE_INFO_TABLE_COLUMNS = [
    { width: 70 },
    { width: 160 },
    { width: 100 },
    { width: 220 },
    { width: 120 },
]

const APP_MEET_WE_INFO_TABLE_MIN_WIDTH = APP_MEET_WE_INFO_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_MEET_WE_INFO_IMAGE_SX = {
    width: 96,
    height: 64,
    objectFit: 'cover',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '4px',
    display: 'block',
}

const APP_MEET_WE_INFO_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

const APP_MEET_WE_INFO_COUNTRY_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

export default function AppMeetWeInfos({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appMeetWeInfo/getList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 우리끼리대회 안내 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 우리끼리대회 안내 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect(prevSearchSelect => ({
            ...prevSearchSelect,
            [name]: value
        }))
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const hasActiveFilters = Boolean(
        searchQuery ||
        searchSelect.searchType
    )

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 우리끼리대회 안내 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 우리끼리대회 안내가 없습니다."
                    description="국가코드 검색어와 대륙 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 App 우리끼리대회 안내가 없습니다."
                description="App 우리끼리대회 안내를 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appMeetWeInfo/delete', { meetWeInfoId: id })

                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)

                    if (params.length === 1 && curPage > 1) {
                        setCurPage(curPage - 1)
                        return
                    }

                    await getData(false)
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 우리끼리대회 안내를 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 우리끼리대회 안내 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="국가코드"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="대륙"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={''}>전체</MenuItem>
                                <MenuItem value={'asia'}>아시아</MenuItem>
                                <MenuItem value={'oceania'}>오세아니아</MenuItem>
                                <MenuItem value={'n-america'}>북아메리카</MenuItem>
                                <MenuItem value={'s-america'}>남아메리카</MenuItem>
                                <MenuItem value={'europe'}>유럽</MenuItem>
                                <MenuItem value={'africa'}>아프리카</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appInfoMeetWe',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_MEET_WE_INFO_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_MEET_WE_INFO_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-meet-we-info-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>타입</TableCell>
                                <TableCell>국가</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.meetWeInfoId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>
                                            {row.image ? (
                                                <img
                                                    src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                                                    alt=""
                                                    style={APP_MEET_WE_INFO_IMAGE_SX}
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell sx={APP_MEET_WE_INFO_LABEL_CELL_SX}>{row.category === 0 ? 'GPS' : 'QR'}</TableCell>
                                        {/* <TableCell>{row.cCode === 'kr' ? '국내' : '해외'}</TableCell> */}
                                        <TableCell sx={APP_MEET_WE_INFO_COUNTRY_CELL_SX}>{row.nameKr || '-'}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={actionLoading}>
                                                <EditIcon path={'appInfoMeetWe'} id={row.meetWeInfoId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.meetWeInfoId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
